import axios from 'axios';
import store from '@/store';
import cmpLogin from '@/utils/cmpLogin';
import { scpmUtil } from '@/utils/scpmUtil';
import i18nMixin from '@/mixins/i18n.mixin.js';
import qs from 'qs';
import messageError from '@/utils/messageError';
import { getBaseURLWithRegion } from '@/utils/cpmRegion';
import { AuthorizationResult } from '@/store/modules/auth';

axios.defaults.baseURL = getBaseURLWithRegion();
//axios.defaults.baseURL = `${process.env.VUE_APP_REST_PROTOCOL}//${process.env.VUE_APP_REST_SUBDOMAIN}.${process.env.VUE_APP_REGION}.${process.env.VUE_APP_REST_DOMAIN}`;
axios.defaults.timeout = 180000;
axios.defaults.maxRedirects = 10;
axios.defaults.paramsSerializer = (params) => {
  return qs.stringify(params);
};
axios.defaults.headers.common['Content-Type'] = `application/json`;
// if (process.env.VUE_APP_USE_IDP !== 'true')
//   axios.defaults.headers.common['Authorization'] = 'Bearer ' + localStorage.getItem('idToken');

export default {
  $t: i18nMixin.methods.t,
  useFake: true,
  loadingCnt: 0,
  processingProjectError: false,

  loadStart() {
    if (this.loadingCnt === 0) {
      if (window.app.$loader) window.app.$loader.open('loading');
    }
    this.loadingCnt++;
  },

  loadEnd() {
    this.loadingCnt--;
    if (this.loadingCnt === 0) {
      if (window.app.$loader) window.app.$loader.close('loading');
    }
  },

  process(
    axiosFunc,
    loadingBar = true,
    ignoreError = false,
    needHandleAuthorizationCheck = false,
    listMessageIgnoreError = [],
    config = {}
  ) {
    if (loadingBar) this.loadStart();
    return new Promise((resolve, reject) => {
      axiosFunc
        .then((data) => {
          resolve(data.data);
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 403) {
            if (!this.isProjectError(error)) {
              if (this.isAuthResourceError(error) && needHandleAuthorizationCheck) {
                resolve(error.response.data);
              } else {
                const AUTH_ERROR = ['AccessDenied'];
                const {
                  response: {
                    data: { code: errorCode }
                  }
                } = error;
                const { handledAuthError } = config;
                const isAuthErrorAndHandled = AUTH_ERROR.includes(errorCode) && handledAuthError;
                if (!isAuthErrorAndHandled) this.apiErrorPopup(error);
                reject(error.response.data);
              }
            }
            return;
          }
          if (this.isAuthError(error)) {
            resolve(error);
          } else {
            if (
              ignoreError &&
              !['PLATFORM-IAM-000021', 'ProjectAccessDenied'].includes(error.response.data.code)
            ) {
              resolve(error);
            } else if (this.isAuthResourceError(error) && needHandleAuthorizationCheck) {
              resolve(error.response.data);
            } else if (listMessageIgnoreError.includes(error.response.data.code)) {
              resolve(error);
            } else {
              this.apiErrorPopup(error);
              reject();
            }
          }
        })
        .finally(() => {
          if (loadingBar) this.loadEnd();
        });
    });
  },

  get(
    url,
    param = {},
    loadingBar = true,
    ignoreError = false,
    ext = {},
    needHandleAuthorizationCheck = false,
    listMessageIgnoreError = [],
    config = {}
  ) {
    return this.process(
      axios.get(url, { params: param, ...ext }, ext),
      loadingBar,
      ignoreError,
      needHandleAuthorizationCheck,
      listMessageIgnoreError,
      config
    );
  },

  delete(
    url,
    param = {},
    body = {},
    loadingBar = true,
    ignoreError = false,
    ext = {},
    needHandleAuthorizationCheck = false,
    listMessageIgnoreError = [],
    config = {}
  ) {
    return this.process(
      axios.delete(url, { params: param, data: body, ...ext }),
      loadingBar,
      ignoreError,
      needHandleAuthorizationCheck,
      listMessageIgnoreError,
      config
    );
  },

  post(
    url,
    param = {},
    loadingBar = true,
    ignoreError = false,
    ext = {},
    needHandleAuthorizationCheck = false,
    listMessageIgnoreError = [],
    config = {}
  ) {
    return this.process(
      axios.post(url, param, ext),
      loadingBar,
      ignoreError,
      needHandleAuthorizationCheck,
      listMessageIgnoreError,
      config
    );
  },

  put(
    url,
    param = {},
    loadingBar = true,
    ignoreError = false,
    ext = {},
    needHandleAuthorizationCheck = false,
    listMessageIgnoreError = [],
    config = {}
  ) {
    return this.process(
      axios.put(url, param, ext),
      loadingBar,
      ignoreError,
      needHandleAuthorizationCheck,
      listMessageIgnoreError,
      config
    );
  },

  isAuthResourceError(error) {
    if (error && error.response && error.response.data) {
      const AuthorizationResultErrorTypes = [
        AuthorizationResult.RESOURCE_NOT_FOUND,
        AuthorizationResult.ACCESS_DENIED
      ];
      if (AuthorizationResultErrorTypes.includes(error.response.data.code)) return true;
    }
    return false;
  },

  isAuthError(error) {
    if (error && error.response && error.response.status === 401) {
      let msg = this.$t('errorCode.sessionExpired');
      const statusCode = error.response.status;
      const code = error.response.data.code;
      const errorCodeStatus = `${code}-${statusCode}`;
      if (messageError.returnMessage(errorCodeStatus)) {
        msg = this.$t(messageError.returnMessage(errorCodeStatus));
      }

      if (typeof window.app.$modal !== 'undefined') {
        window.app.$modal.open({
          rootId: 'app',
          type: 'alert',
          title: this.$t('title.notice'),
          message: msg,
          action: () => {
            cmpLogin.logout();
          }
        });
      }

      return true;
    }

    return false;
  },

  isProjectError(error) {
    if (
      error &&
      error.response &&
      // error.response.status == 403 &&
      ['PLATFORM-IAM-000021', 'ProjectAccessDenied'].includes(error.response.data.code) &&
      !this.processingProjectError
    ) {
      this.processingProjectError = true;
      if (typeof window.app.$modal !== 'undefined') {
        const close = () => {
          if (process.env.VUE_APP_USE_IDP == 'true') {
            window.location.href = '/monitoring-console/';
          } else {
            window.location.href = '/';
          }
          this.processingProjectError = false;
        };
        window.app.$modal.open({
          rootId: 'app',
          type: 'alert',
          title: this.$t('title.notice'),
          message: this.$t('errorCode.ProjectAccessDenied'),
          action: close,
          cancel: close
        });
      }
      return true;
    } else {
      return false;
    }
  },

  getUserEmail() {
    const userEmail = window.sessionStorage.getItem('userEmail') || 'gilding.hong@samsung.com';
    return this.$t('errorCode.authorizationCheck.deny', [userEmail]);
  },
  async apiErrorPopup(error) {
    let title = this.$t('title.notice');
    let msg = this.$t('message.networkConnectFail');
    try {
      window.app.$modal.close();
    } catch (e) {
      // console.log(e);
    }
    try {
      let code;
      if (error.request.responseType === 'blob') {
        const data = await this.blobToObject(error.response.data);
        code = data.code.trim();
        if (code === 'QuerySizeExceeded') {
          const downloadScopes = data.params;
          const maxCount =
            downloadScopes.find((downloadScope) => downloadScope.name === 'maxCount').value ||
            32676;
          msg = this.$t('page.performance.downloadCheckCharactersRawData', {
            param: Number(maxCount).toLocaleString()
          });
        }
      } else {
        const statusCode = error.response.status;
        code = error.response.data.code.trim();
        const errCodeMessage = `errorCode.${code}`;

        if (this.$t(errCodeMessage) !== errCodeMessage) {
          msg = this.$t(errCodeMessage);
        }

        const errorCodeStatus = `${code}-${statusCode}`;
        if (messageError.returnMessage(errorCodeStatus)) {
          msg = this.$t(messageError.returnMessage(errorCodeStatus));
        }
        if (code === 'XssFiltered') {
          this.setResetFilterXss();
        }

        if (code === 'PLATFORM-IAM-000021') {
          msg = this.$t('errorCode.ProjectAccessDenied');
        }

        if (code === 'InvalidQueryDtValue') {
          msg = this.$t('message.storePerformance');
        }

        if (code === 'AccessDenied') {
          title = this.$t('errorCode.authorizationCheck.deny_title');
          const tranUserEmail = this.getUserEmail();
          msg = scpmUtil.authErrorMessage(error.response.data.params, tranUserEmail);
        }

        if (code === 'QuerySizeExceeded') {
          const { params: downloadScopes } = error.response.data;
          const totalCount =
            downloadScopes.find((downloadScope) => downloadScope.name === 'totalCount').value || 0;
          const maxCount =
            downloadScopes.find((downloadScope) => downloadScope.name === 'maxCount').value || 0;
          msg = this.$t('page.performance.downloadCheckRawData', {
            params: `${totalCount}/${maxCount}`
          });
        }
      }

      if (!msg) {
        msg = this.$t('message.networkConnectFail');
      }
    } catch (e) {
      // console.log(e);
    } finally {
      if (typeof window.app.$modal !== 'undefined') {
        window.app.$modal.open({
          rootId: 'app',
          type: 'alert',
          title: title,
          message: msg,
          action: () => {
            store.dispatch('searchStore/setDefaultResetFilterXss');
          },
          cancel: () => {
            store.dispatch('searchStore/setDefaultResetFilterXss');
          }
        });
      }
    }
  },

  setIdToken(idToken) {
    if (idToken == null) return;
    localStorage.setItem('idToken', idToken);
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + idToken;
  },

  setRoleId(roleId) {
    axios.defaults.headers.common['X-Cmp-RoleId'] = roleId;
  },

  getRoleId() {
    return axios.defaults.headers.common['X-Cmp-RoleId'];
  },

  setLanguageHeaders(language) {
    axios.defaults.headers.common['X-Nuri-Language'] = language;
  },

  setResetFilterXss() {
    store.dispatch('searchStore/setResetFilterXss');
  },

  async blobToObject(blob) {
    const text = await blob.text();
    return JSON.parse(text);
  }
};
